import type {
	ChatBubbleVariant,
	ChatWidgetButtonVariant,
	RadiusVariant,
	SpaceVariant,
} from '@atlassian/csm-widget-ui-components/types';

import type { WidgetConfig } from '../../../services/assistance-service/types';

import type { WidgetBrandingTheme, WidgetResponse } from './types';

/**
 * Type guard to check if a response is a WidgetConfigV2Response
 */
export const isWidgetConfigV2Response = (
	response: WidgetResponse | WidgetConfig,
): response is WidgetResponse => {
	return 'widget' in response && 'agent' in response && 'customerHubBranding' in response;
};

/**
 * Normalizes the color vibe variant from the API to the expected format
 */
const normalizeColorVibeVariant = (
	variant: WidgetBrandingTheme['colorVibeVariant'] | undefined,
): ChatWidgetButtonVariant => {
	const normalized = variant?.toLowerCase();
	if (normalized === 'vibrant' || normalized === 'light' || normalized === 'dark') {
		return normalized;
	}
	return 'vibrant';
};

/**
 * Normalizes the radius variant from the API to the expected format
 */
const normalizeRadius = (radius: WidgetBrandingTheme['radius'] | undefined): RadiusVariant => {
	const normalized = radius?.toLowerCase();
	if (normalized === 'normal' || normalized === 'rounded' || normalized === 'sharp') {
		return normalized;
	}
	return 'normal';
};

/**
 * Normalizes the space variant from the API to the expected format
 */
const normalizeSpace = (space: WidgetBrandingTheme['space'] | undefined): SpaceVariant => {
	const normalized = space?.toLowerCase();
	if (normalized === 'comfortable' || normalized === 'cosy' || normalized === 'generous') {
		return normalized;
	}
	return 'comfortable';
};

/**
 * Normalizes the chat color variant from the API to the expected format
 */
const normalizeChatColor = (
	chatColor: WidgetBrandingTheme['chatColor'] | undefined,
): ChatBubbleVariant => {
	const normalized = chatColor?.toLowerCase();
	if (
		normalized === 'default' ||
		normalized === 'light' ||
		normalized === 'dark' ||
		normalized === 'vibrant'
	) {
		return normalized;
	}
	return 'default';
};

/**
 * Transforms the new V2 API response to the legacy format that the existing codebase expects
 *
 * Notes: We want to use the default #B5B5B5 when there is no primaryColor. This is because not all
 * tenants have the "Branding" feature enabled. This will throw an error downstream when generating
 * the colour variants if no value is provided.
 */
export const transformWidgetConfigV2ToLegacy = (v2Response: WidgetResponse): WidgetConfig => {
	return {
		id: v2Response.widget.id || '',
		allowedDomains: v2Response.widget.allowedDomains || [],
		isAnonymousAccessEnabled: v2Response.widget.isAnonymousAccessEnabled,
		isWidgetEnabled: v2Response.widget.isEnabled,
		agentId: v2Response.agent?.id || '',
		csmAiHubId: v2Response.widget.csmAiHubId || null,
		customerHubId: v2Response.customerHubBranding?.customerHubId || null,
		widgetTheme: {
			branding: {
				logo: v2Response.customerHubBranding?.logo || '',
				logoUrl: v2Response.customerHubBranding?.logoUrl || '',
				brandingTheme: {
					primaryColor: v2Response.customerHubBranding?.primaryColor || '#B5B5B5',
					colorVibeVariant: normalizeColorVibeVariant(
						v2Response.widget.brandingTheme.colorVibeVariant,
					),
					radius: normalizeRadius(v2Response.widget.brandingTheme.radius),
					space: normalizeSpace(v2Response.widget.brandingTheme.space),
					chatColor: normalizeChatColor(v2Response.widget.brandingTheme.chatColor),
					attribution: v2Response.widget.brandingTheme.attribution
						? {
								text: v2Response.widget.brandingTheme.attribution.text || null,
								url: v2Response.widget.brandingTheme.attribution.url || null,
							}
						: null,
					...(v2Response.widget.brandingTheme.welcomeMessage
						? { welcomeMessage: v2Response.widget.brandingTheme.welcomeMessage }
						: {}),
					icon: v2Response.widget.brandingTheme.icon
						? {
								url: v2Response.widget.brandingTheme.icon.url || null,
								type: v2Response.widget.brandingTheme.icon.type,
							}
						: { url: null, type: 'DEFAULT' },
					tile: v2Response.widget.brandingTheme.tile
						? {
								url: v2Response.widget.brandingTheme.tile.url || null,
								type: v2Response.widget.brandingTheme.tile.type,
							}
						: { url: null, type: 'DEFAULT' },
				},
			},
			messages: {
				welcomeMessage: v2Response.agent?.greetingMessage || '',
				conversationStarters: v2Response.agent?.userDefinedConversationStarters || [],
			},
		},
		// consentForm may be absent from the response, so only add it to the map when present
		...(v2Response.widget.consentForm && { consentForm: v2Response.widget.consentForm }),
	};
};
